/** Organization-type role catalog.
 *  Single source of truth for the invitation role picker. Kept in sync with
 *  the backend by convention — the backend must also validate roles against
 *  org.type; the frontend just enforces safe UX.
 */

export const ORG_TYPES = ["business", "care", "internal"];

export const BUSINESS_ROLES = [
  { value: "employee",        en: "Employee",              nl: "Medewerker" },
  { value: "flm_admin",       en: "Team Leader (FLM)",     nl: "Teamleider (FLM)" },
  { value: "hr_admin",        en: "HR Administrator",      nl: "HR-beheerder" },
  { value: "business_admin",  en: "Business Administrator", nl: "Zakelijke beheerder" },
];

export const CARE_ROLES = [
  { value: "client",          en: "Client",             nl: "Cliënt" },
  { value: "therapist",       en: "Therapist",          nl: "Therapeut" },
  { value: "psychologist",    en: "Psychologist",       nl: "Psycholoog" },
  { value: "psychiatrist",    en: "Psychiatrist",       nl: "Psychiater" },
  { value: "care_coordinator", en: "Care Coordinator",  nl: "Zorgcoördinator" },
  { value: "care_admin",      en: "Care Administrator", nl: "Zorgbeheerder" },
];

export const PLATFORM_ROLES = [
  { value: "platform_owner",  en: "Platform Owner", nl: "Platformeigenaar" },
  { value: "super_admin",     en: "Super Admin",    nl: "Superbeheerder" },
];

/** Return the correct role list for a given org type / context.
 *  `orgType`: "business" | "care" | "internal" | "platform"
 *  Falls back to an empty list for unknown types (safer than mixing). */
export function rolesForOrgType(orgType) {
  const t = String(orgType || "").toLowerCase();
  if (t === "care" || t === "ggz") return CARE_ROLES;
  if (t === "business" || t === "corporate") return BUSINESS_ROLES;
  if (t === "internal" || t === "platform") return PLATFORM_ROLES;
  return [];
}

/** Localized label helper. */
export function labelFor(role, lang = "en") {
  const all = [...BUSINESS_ROLES, ...CARE_ROLES, ...PLATFORM_ROLES];
  const item = all.find((r) => r.value === role);
  if (!item) return role;
  return lang === "nl" ? item.nl : item.en;
}

/** Given a role value, which org type does it belong to?
 *  Used for portal-access enforcement. */
export function orgTypeForRole(role) {
  const r = String(role || "").toLowerCase();
  if (BUSINESS_ROLES.some((x) => x.value === r)) return "business";
  if (CARE_ROLES.some((x) => x.value === r)) return "care";
  if (PLATFORM_ROLES.some((x) => x.value === r)) return "platform";
  return null;
}

/** Guard predicate: does the *user's* role set allow access to a portal?
 *  Business ↛ Care.  Care ↛ HR.  Platform → Everything. */
export function canAccessPortal(userRoles, portal) {
  if (!userRoles) return false;
  const has = (k) => userRoles.has(k);
  if (has("platform_owner") || has("super_admin")) return true;
  if (portal === "hr" || portal === "business") {
    return BUSINESS_ROLES.some((r) => has(r.value)) || has("hr") || has("manager") || has("org_admin");
  }
  if (portal === "care") {
    return CARE_ROLES.some((r) => has(r.value)) || has("clinician");
  }
  if (portal === "client") return true; // everyone gets a client space
  return false;
}
